import { splitOptions } from '@eliadmin/core/src/utils/nt/text';

/**
 * The rules typed in the administration („Konto - pravidlá hesla“: „Aspoň 8 znakov | Veľké aj malé
 * písmeno | Aspoň jedno číslo | Špeciálny znak (odporúčané)“) are checked by position, like the
 * icons of the trust row: the first three are required, the last one (marked „odporúčané“) only
 * raises the strength. A rule the administration does not list is not checked.
 */
const CHECKS: { test: (password: string) => boolean; required: boolean }[] = [
  { test: password => password.length >= 8, required: true },
  { test: password => /\p{Ll}/u.test(password) && /\p{Lu}/u.test(password), required: true },
  { test: password => /\d/.test(password), required: true },
  { test: password => /[^\p{L}\d\s]/u.test(password), required: false },
];

export type PasswordRule = { label: string; met: boolean; required: boolean };

export function passwordRules(rulesText: string, password: string): PasswordRule[] {
  return splitOptions(rulesText)
    .slice(0, CHECKS.length)
    .map((label, index) => ({
      label,
      met: CHECKS[index].test(password),
      required: CHECKS[index].required,
    }));
}

/** Every required rule the administration lists is met. */
export function meetsRequiredRules(rulesText: string, password: string) {
  return passwordRules(rulesText, password).every(rule => rule.met || !rule.required);
}

/**
 * Strength for the meter: one bar per rule, filled as many as are met; the level from the admin
 * list „Slabé | Stredné | Silné“ – strong once every required rule is met, medium with at least two
 * rules, weak otherwise. `null` for an empty password.
 */
export function passwordStrength(rulesText: string, password: string) {
  if (!password) return null;
  const rules = passwordRules(rulesText, password);
  const met = rules.filter(rule => rule.met).length;
  const level = rules.every(rule => rule.met || !rule.required) ? 2 : met >= 2 ? 1 : 0;
  return { bars: rules.length, met, level: level as 0 | 1 | 2 };
}
